import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../utils/api';

const EditarPerfil = ({ adminMode = false }) => {
    const { uuid } = useParams();
    const navigate = useNavigate();
    const fotoRef = useRef();

    const [perfil, setPerfil] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [uploadingFoto, setUploadingFoto] = useState(false);
    const [mensaje, setMensaje] = useState(null);
    const [error, setError] = useState(null);

    const apiBase = adminMode ? `/tarjetas/${uuid}/perfil` : `/perfil/${uuid}`;
    const backUrl  = adminMode ? '/tarjetas' : '/mis-tarjetas';

    useEffect(() => {
        // Para admin cargamos desde /tarjetas/:uuid/perfil (GET no existe aún),
        // usamos el endpoint de cliente pero con el uuid de la tarjeta
        const loadUrl = adminMode ? `/perfil/${uuid}` : `/perfil/${uuid}`;
        api.get(loadUrl)
            .then(({ data }) => setPerfil(data))
            .catch(() => navigate(backUrl))
            .finally(() => setLoading(false));
    }, [uuid, navigate, backUrl, adminMode]);

    const handleChange = (e) => {
        setPerfil(prev => ({ ...prev, [e.target.name]: e.target.value }));
    };

    const handleSave = async () => {
        setSaving(true);
        setMensaje(null);
        setError(null);
        try {
            await api.put(apiBase, perfil);
            setMensaje('Perfil guardado correctamente ✓');
        } catch (e) {
            setError(e.response?.data?.error || 'Error guardando el perfil');
        } finally {
            setSaving(false);
        }
    };

    const handleFoto = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setUploadingFoto(true);
        const form = new FormData();
        form.append('foto', file);
        try {
            const { data } = await api.post(`/perfil/${uuid}/foto`, form, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            setPerfil(prev => ({ ...prev, foto_url: data.foto_url }));
        } catch (e) {
            setError(e.response?.data?.error || 'Error subiendo foto');
        } finally {
            setUploadingFoto(false);
        }
    };

    if (loading) return (
        <div className="flex items-center justify-center h-64">
            <div className="text-gray-400 text-sm">Cargando perfil...</div>
        </div>
    );

    const previewUrl = `${window.location.origin}/c/${perfil?.codigo}`;

    return (
        <div className="max-w-3xl mx-auto">
            <div className="mb-6 flex items-center gap-3">
                <button onClick={() => navigate(backUrl)} className="text-gray-400 hover:text-gray-600 text-xl">←</button>
                <div>
                    <h1 className="text-2xl font-bold text-gray-800">
                        {adminMode ? 'Editar Perfil (Admin)' : 'Editar Perfil'}
                    </h1>
                    <p className="text-xs text-gray-400 font-mono mt-0.5">{perfil?.codigo}</p>
                </div>
                <a href={previewUrl} target="_blank" rel="noreferrer"
                   className="ml-auto text-sm text-blue-500 hover:underline">
                    Ver perfil público →
                </a>
            </div>

            {mensaje && <div className="mb-4 p-3 bg-green-50 text-green-700 rounded-lg text-sm">{mensaje}</div>}
            {error   && <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-lg text-sm">{error}</div>}

            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 space-y-6">

                {/* Foto */}
                <div className="flex items-center gap-5">
                    <div className="w-20 h-20 rounded-full overflow-hidden bg-gray-100 flex-shrink-0 border-2 border-gray-200">
                        {perfil?.foto_url ? (
                            <img src={perfil.foto_url} alt="Foto" className="w-full h-full object-cover" />
                        ) : (
                            <div className="w-full h-full flex items-center justify-center text-3xl text-gray-300">👤</div>
                        )}
                    </div>
                    <div>
                        <p className="text-sm font-medium text-gray-700 mb-1">Foto de perfil</p>
                        <p className="text-xs text-gray-400 mb-2">JPG, PNG o WEBP — máx 3MB</p>
                        <button onClick={() => fotoRef.current.click()} disabled={uploadingFoto}
                            className="px-3 py-1.5 text-sm bg-gray-100 hover:bg-gray-200 rounded-lg text-gray-700 disabled:opacity-50">
                            {uploadingFoto ? 'Subiendo...' : 'Cambiar foto'}
                        </button>
                        <input ref={fotoRef} type="file" accept="image/*" className="hidden" onChange={handleFoto} />
                    </div>
                </div>

                {/* Datos personales */}
                <div>
                    <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">Datos personales</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm text-gray-600 mb-1">Nombre</label>
                            <input name="nombre" value={perfil?.nombre || ''} onChange={handleChange}
                                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300" />
                        </div>
                        <div>
                            <label className="block text-sm text-gray-600 mb-1">Apellido</label>
                            <input name="apellido" value={perfil?.apellido || ''} onChange={handleChange}
                                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300" />
                        </div>
                        <div>
                            <label className="block text-sm text-gray-600 mb-1">Cargo</label>
                            <input name="cargo" value={perfil?.cargo || ''} onChange={handleChange}
                                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300" />
                        </div>
                        <div>
                            <label className="block text-sm text-gray-600 mb-1">Empresa</label>
                            <input name="empresa" value={perfil?.empresa || ''} onChange={handleChange}
                                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300" />
                        </div>
                    </div>
                </div>

                {/* Contacto */}
                <div>
                    <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">Contacto</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm text-gray-600 mb-1">📞 Teléfono</label>
                            <input name="telefono" value={perfil?.telefono || ''} onChange={handleChange}
                                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300" />
                        </div>
                        <div>
                            <label className="block text-sm text-gray-600 mb-1">💬 WhatsApp</label>
                            <input name="whatsapp" value={perfil?.whatsapp || ''} onChange={handleChange}
                                placeholder="573001234567"
                                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300" />
                        </div>
                        <div>
                            <label className="block text-sm text-gray-600 mb-1">✉️ Email</label>
                            <input name="email" type="email" value={perfil?.email || ''} onChange={handleChange}
                                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300" />
                        </div>
                        <div>
                            <label className="block text-sm text-gray-600 mb-1">🌐 Sitio web</label>
                            <input name="website" value={perfil?.website || ''} onChange={handleChange}
                                placeholder="https://miweb.com"
                                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300" />
                        </div>
                    </div>
                </div>

                {/* Redes sociales */}
                <div>
                    <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">Redes sociales</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm text-gray-600 mb-1">LinkedIn</label>
                            <input name="linkedin" value={perfil?.linkedin || ''} onChange={handleChange}
                                placeholder="https://linkedin.com/in/usuario"
                                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300" />
                        </div>
                        <div>
                            <label className="block text-sm text-gray-600 mb-1">Instagram</label>
                            <input name="instagram" value={perfil?.instagram || ''} onChange={handleChange}
                                placeholder="https://instagram.com/usuario"
                                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300" />
                        </div>
                        <div className="sm:col-span-2">
                            <label className="block text-sm text-gray-600 mb-1">YouTube</label>
                            <input name="youtube" value={perfil?.youtube || ''} onChange={handleChange}
                                placeholder="https://youtube.com/@canal"
                                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300" />
                        </div>
                    </div>
                </div>

                {/* Bio */}
                <div>
                    <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">Biografía profesional</h3>
                    <textarea name="bio" value={perfil?.bio || ''} onChange={handleChange} rows={4}
                        placeholder="Cuéntale a la gente quién eres, qué haces y qué ofreces..."
                        className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300 resize-none" />
                </div>

                {/* Guardar */}
                <div className="flex justify-end pt-2">
                    <button onClick={handleSave} disabled={saving}
                        className="px-6 py-2.5 rounded-lg text-white font-medium text-sm disabled:opacity-50 transition"
                        style={{ backgroundColor: 'var(--color-primary)' }}>
                        {saving ? 'Guardando...' : 'Guardar cambios'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default EditarPerfil;
